import { useMemo } from 'react';

import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { ensureListing, ensureOwnListing } from '../../util/data';
import useAppSelector from '../../util/hooks/useAppSelector';

/**
 * Selects the current listing from marketplaceData with a stable object
 * reference while `state.marketplaceData.entities` is unchanged, so the
 * memo()ed sections receiving it as a prop can skip re-renders.
 *
 * The memo key is the top-level `entities` object: the reducer replaces it
 * on every ADD_MARKETPLACE_ENTITIES, while the nested per-type maps are
 * mutated in place (see updatedEntities in util/data.js) — never key on those.
 *
 * @param {import('@vansite/ts-sharetribe-flex-sdk/types').UUID} listingId
 *   referentially stable UUID instance (memoize at the call site)
 * @param {boolean} isOwnVariant true for the draft/pendingApproval variants
 * @returns {object} ensured listing (or own listing)
 */
const useCurrentListing = (listingId, isOwnVariant) => {
  const entities = useAppSelector((state) => state.marketplaceData.entities);

  return useMemo(() => {
    const type = isOwnVariant ? 'ownListing' : 'listing';
    const listings = getMarketplaceEntities(entities, [{ id: listingId, type }]);
    const listing = listings.length === 1 ? listings[0] : null;
    return isOwnVariant ? ensureOwnListing(listing) : ensureListing(listing);
  }, [entities, listingId, isOwnVariant]);
};

export default useCurrentListing;
